import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Sparkles, Gauge, AlertTriangle, FileDown, Mail, Check, Pencil, Share2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, km, dateFr, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const LABELS = { neutre: "À renseigner", vert: "À jour", orange: "Bientôt", rouge: "Dépassé" };

const empty = {
  nom: "", dernier_km: 0, derniere_date: new Date().toISOString().slice(0, 10),
  intervalle_km: "", intervalle_mois: "",
};

const echeanceText = (it) => {
  if (it.statut === "rouge") return "Dépassé";
  const parts = [];
  if (it.km_restant != null) parts.push(`dans ${km(it.km_restant)}`);
  if (it.mois_restant != null)
    parts.push(it.mois_restant < 1 ? `dans ${it.jours_restant} jours` : `dans ${Math.round(it.mois_restant)} mois`);
  return parts.join(" · ") || "—";
};

const SuiviCard = ({ it, onDelete, onEdit }) => (
  <div data-testid={`suivi-card-${it.id}`}
    className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:-translate-y-0.5 transition-transform">
    <div className="flex items-start justify-between gap-3">
      <h3 className="font-display text-lg font-semibold text-slate-900">{it.nom}</h3>
      <StatusBadge status={it.statut} label={LABELS[it.statut]} testId={`suivi-status-${it.id}`} />
    </div>
    <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
      <div>
        <dt className="text-xs font-bold uppercase tracking-wider text-slate-400">Prochaine échéance km</dt>
        <dd className="text-slate-700">{it.prochain_km != null ? km(it.prochain_km) : "—"}</dd>
      </div>
      <div>
        <dt className="text-xs font-bold uppercase tracking-wider text-slate-400">Prochaine échéance date</dt>
        <dd className="text-slate-700">{it.prochaine_date ? dateFr(it.prochaine_date) : "—"}</dd>
      </div>
    </dl>
    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
      <span data-testid={`suivi-eta-${it.id}`}
        className={`text-sm font-semibold ${it.statut === "rouge" ? "text-red-600" : it.statut === "orange" ? "text-amber-600" : "text-emerald-600"}`}>
        {echeanceText(it)}
      </span>
      {onEdit && (
        <div className="flex items-center gap-3">
          <button onClick={() => onEdit(it)} data-testid={`suivi-edit-${it.id}`}
            className="text-slate-400 hover:text-blue-600 transition-colors">
            <Pencil className="h-4 w-4" />
          </button>
          {onDelete && (
            <button onClick={() => onDelete(it.id)} data-testid={`suivi-delete-${it.id}`}
              className="text-slate-400 hover:text-red-600 transition-colors">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
    <p className="mt-3 text-xs text-slate-400">
      Dernière intervention : {dateFr(it.derniere_date)} à {km(it.dernier_km)}
      {it.intervalle_km ? ` · tous les ${km(it.intervalle_km)}` : ""}
      {it.intervalle_mois ? ` · tous les ${it.intervalle_mois} mois` : ""}
    </p>
  </div>
);

export default function VehicleDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editSuiviId, setEditSuiviId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [kmValue, setKmValue] = useState("");
  const [sending, setSending] = useState(false);
  const [share, setShare] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    api.get(`/vehicles/${id}/share`).then((r) => setShare(r.data)).catch(() => setShare(null));
  }, [id]);

  const toggleShare = async () => {
    try {
      if (share?.enabled) {
        await api.delete(`/vehicles/${id}/share`);
        setShare({ enabled: false, url: null });
        toast.success("Lien de partage désactivé");
      } else {
        const { data: s } = await api.post(`/vehicles/${id}/share`);
        setShare({ enabled: true, url: s.url });
        toast.success("Lien de partage activé");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(share.url);
      toast.success("Lien copié");
    } catch {
      toast.info(share.url);
    }
  };

  const fetchSuggestion = async (nom) => {
    if (!nom) return setSuggestion(null);
    try {
      const { data: d } = await api.get("/periodicites", {
        params: { vehicle_id: id, type_entretien: nom },
      });
      setSuggestion(d.suggestion);
    } catch {
      setSuggestion(null);
    }
  };

  const applySuggestion = () => {
    setForm((f) => ({
      ...f,
      intervalle_km: suggestion.intervalle_km ?? f.intervalle_km,
      intervalle_mois: suggestion.intervalle_mois ?? f.intervalle_mois,
    }));
    toast.success("Suggestion appliquée, vous pouvez l'ajuster");
  };

  const load = useCallback(
    () => api.get(`/vehicles/${id}/etat-entretien`).then((r) => setData(r.data)),
    [id]
  );
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.intervalle_km && !form.intervalle_mois)
      return toast.error("Indiquez un intervalle en km ou en mois");
    setSaving(true);
    try {
      const payload = {
        nom: form.nom,
        dernier_km: Number(form.dernier_km) || 0,
        derniere_date: form.derniere_date,
        intervalle_km: form.intervalle_km ? Number(form.intervalle_km) : null,
        intervalle_mois: form.intervalle_mois ? Number(form.intervalle_mois) : null,
      };
      if (editSuiviId) {
        await api.put(`/etat-entretien/${editSuiviId}`, payload);
        toast.success("Entretien suivi modifié");
      } else {
        await api.post(`/vehicles/${id}/etat-entretien`, payload);
        toast.success("Entretien suivi ajouté");
      }
      setForm(empty);
      setEditSuiviId(null);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const startCreateSuivi = () => {
    setEditSuiviId(null);
    setForm(empty);
    setSuggestion(null);
    setOpen(true);
  };

  const startEditSuivi = (it) => {
    setEditSuiviId(it.id);
    setSuggestion(null);
    setForm({
      nom: it.nom,
      dernier_km: it.dernier_km || 0,
      derniere_date: (it.derniere_date || "").slice(0, 10),
      intervalle_km: it.intervalle_km || "",
      intervalle_mois: it.intervalle_mois || "",
    });
    setOpen(true);
  };

  const loadDemo = async () => {
    try {
      await api.post(`/vehicles/${id}/etat-entretien/demo`);
      toast.success("Données de démonstration chargées");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (sid) => {
    await api.delete(`/etat-entretien/${sid}`);
    toast.success("Supprimé");
    load();
  };

  const saveKm = async () => {
    const value = Number(kmValue);
    if (!kmValue || Number.isNaN(value) || value < 0) return toast.error("Kilométrage invalide");
    try {
      const { data: fresh } = await api.patch(`/vehicles/${id}/kilometrage`, { kilometrage: value });
      setData(fresh);
      setKmValue("");
      toast.success("Kilométrage mis à jour, échéances recalculées");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/vehicles/${id}/carnet-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "carnet-entretien.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Carnet PDF téléchargé");
    } catch (err) {
      toast.error("Export PDF impossible");
    }
  };

  const sendReminder = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post("/reminders/send");
      toast[r.sent ? "success" : "info"](
        r.sent ? "Récapitulatif envoyé par email" : r.message
      );
    } catch (err) {
      const code = err.response?.status;
      toast.error(code === 429
        ? "Limite d'envoi d'emails atteinte, réessayez plus tard."
        : formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!data) return <p className="text-slate-500">Chargement…</p>;
  const v = data.vehicle;

  return (
    <div data-testid="vehicle-detail-page" className="space-y-8">
      <div className="animate-fade-up">
        <Link to="/app/vehicules" data-testid="back-to-vehicles"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Mes véhicules
        </Link>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Fiche véhicule</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {v.marque} {v.modele}
            </h1>
            <p className="mt-2 inline-flex items-center gap-2 text-slate-600">
              <Gauge className="h-4 w-4 text-slate-400" /> {km(v.kilometrage)} · {v.annee}
              {v.carburant ? ` · ${v.carburant}` : ""}
            </p>
            {v.immatriculation && (
              <p data-testid="vehicle-immatriculation" className="mt-1 inline-block px-2.5 py-1 rounded-md border border-slate-300 bg-slate-50 font-display text-sm font-bold tracking-widest text-slate-700">
                {v.immatriculation}
              </p>
            )}          </div>
          <StatusBadge status={data.statut_global} label={LABELS[data.statut_global]} testId="vehicle-global-status" />
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label>Mettre à jour le compteur</Label>
            <div className="flex items-center gap-2">
              <Input type="number" placeholder={String(v.kilometrage)} value={kmValue}
                onChange={(e) => setKmValue(e.target.value)} data-testid="km-update-input"
                className="w-36" />
              <Button onClick={saveKm} data-testid="km-update-btn" className="rounded-full bg-blue-600 hover:bg-blue-700">
                <Check className="h-4 w-4 mr-1.5" /> Mettre à jour
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <Button variant="outline" onClick={exportPdf} data-testid="export-pdf-btn" className="rounded-full border-slate-200">
              <FileDown className="h-4 w-4 mr-1.5" /> Carnet PDF
            </Button>
            <Button variant="outline" onClick={sendReminder} disabled={sending} data-testid="send-reminder-btn"
              className="rounded-full border-slate-200">
              <Mail className="h-4 w-4 mr-1.5" /> {sending ? <span>Envoi…</span> : <span>M’envoyer le récapitulatif</span>}
            </Button>
            <Button variant="outline" onClick={toggleShare} data-testid="share-toggle-btn"
              className="rounded-full border-slate-200">
              <Share2 className="h-4 w-4 mr-1.5" />
              {share?.enabled ? "Désactiver le partage" : "Partager le carnet"}
            </Button>
          </div>
        </div>

        {share?.enabled && (
          <div data-testid="share-link-box"
            className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4 flex flex-wrap items-center gap-3">
            <p className="text-sm text-slate-700 min-w-0">
              Lien de consultation en lecture seule :{" "}
              <a href={share.url} target="_blank" rel="noreferrer" data-testid="share-link"
                className="font-semibold text-blue-700 break-all hover:underline">{share.url}</a>
              <span className="block text-xs text-slate-500">
                L'acheteur consulte le carnet sans pouvoir le modifier. Désactivable à tout moment.
              </span>
            </p>
            <Button type="button" onClick={copyShare} data-testid="share-copy-btn"
              className="rounded-full bg-blue-600 hover:bg-blue-700 shrink-0">
              <Copy className="h-4 w-4 mr-1.5" /> Copier
            </Button>
          </div>
        )}
      </div>

      {v.fiche_technique && Object.values(v.fiche_technique).some(Boolean) && (
        <section data-testid="fiche-technique-card"
          className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm animate-fade-up">
          <h2 className="font-display text-xl font-semibold text-slate-900">Fiche technique</h2>
          <dl className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 text-sm">
            {[
              ["VIN", v.fiche_technique.vin],
              ["Carrosserie", v.fiche_technique.carrosserie],
              ["Puissance fiscale", v.fiche_technique.puissance_fiscale && `${v.fiche_technique.puissance_fiscale} CV`],
              ["Cylindrée", v.fiche_technique.cylindree && `${v.fiche_technique.cylindree} cm³`],
              ["Genre", v.fiche_technique.genre],
              ["Finition", v.fiche_technique.finition],
              ["Code moteur", v.fiche_technique.code_moteur],
              ["Places", v.fiche_technique.nb_places],
            ]
              .filter(([, val]) => val)
              .map(([k, val]) => (
                <div key={k}>
                  <dt className="text-xs font-bold uppercase tracking-wider text-slate-400">{k}</dt>
                  <dd className="mt-0.5 text-slate-700 break-all">{val}</dd>
                </div>
              ))}
          </dl>
        </section>
      )}

      {data.a_prevoir.length > 0 && (
        <section data-testid="a-prevoir-card"
          className="bg-white border border-amber-200 rounded-xl p-6 shadow-sm animate-fade-up">
          <h2 className="font-display text-xl font-semibold text-slate-900 inline-flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" /> À prévoir prochainement
          </h2>
          <ul className="mt-4 space-y-3">
            {data.a_prevoir.map((it) => (
              <li key={it.id} data-testid={`a-prevoir-item-${it.id}`}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 last:border-0 pb-3 last:pb-0">
                <span className="font-medium text-slate-800">{it.nom}</span>
                <span className="flex items-center gap-3">
                  <span className="text-sm text-slate-500">{echeanceText(it)}</span>
                  <StatusBadge status={it.statut} label={LABELS[it.statut]} />
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="font-display text-2xl font-bold text-slate-800">État de mon entretien</h2>          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={loadDemo} data-testid="load-demo-btn"
              className="rounded-full border-slate-200">
              <Sparkles className="h-4 w-4 mr-1.5" /> Données de démo
            </Button>
            <Button onClick={startCreateSuivi} data-testid="open-suivi-form-btn"
              className="rounded-full bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-1.5" /> Entretien
            </Button>
          </div>
        </div>

        {data.items.length === 0 ? (
          <div data-testid="suivi-empty" className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
            <p className="font-display text-lg font-semibold text-slate-800">Aucun entretien suivi</p>
            <p className="mt-1 text-sm text-slate-500">
              Ajoutez vos intervalles ou chargez les données de démonstration.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="suivi-list">
            {data.items.map((it) => (
              <SuiviCard key={it.id} it={it} onDelete={remove} onEdit={startEditSuivi} />
            ))}
          </div>
        )}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="suivi-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editSuiviId ? "Modifier l'entretien suivi" : "Suivre un entretien"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="suivi-form">
            <div className="space-y-2">
              <Label>Nom de l'opération</Label>
              <Input required value={form.nom} list="suivi-types"
                onChange={(ev) => {
                  setForm({ ...form, nom: ev.target.value });
                  fetchSuggestion(ev.target.value);
                }}
                data-testid="suivi-nom-input" placeholder="Vidange moteur" />
              <datalist id="suivi-types">
                {["Vidange", "Filtre à huile", "Filtre à air", "Filtre à carburant",
                  "Filtre d'habitacle", "Bougies d'allumage", "Liquide de frein",
                  "Plaquettes de frein", "Disques de frein", "Courroie de distribution",
                  "Liquide de refroidissement", "Pneus", "Batterie", "Contrôle technique",
                  "Révision constructeur", "Climatisation"].map((t) => <option key={t} value={t} />)}
              </datalist>
            </div>
            {suggestion && (
              <div data-testid="suivi-suggestion"
                className="rounded-lg border border-blue-200 bg-blue-50 p-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-slate-700">
                  Périodicité couramment observée :{" "}
                  <span className="font-semibold">
                    {[suggestion.intervalle_km && `tous les ${suggestion.intervalle_km.toLocaleString("fr-FR")} km`,
                      suggestion.intervalle_mois && `tous les ${suggestion.intervalle_mois} mois`]
                      .filter(Boolean).join(" ou ")}
                  </span>
                  <span className="block text-xs text-slate-500">
                    Suggestion indicative, à ajuster ou ignorer librement.
                  </span>
                </p>
                <Button type="button" onClick={applySuggestion} data-testid="suivi-apply-suggestion-btn"
                  className="rounded-full bg-blue-600 hover:bg-blue-700 shrink-0">
                  Appliquer
                </Button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dernier kilométrage</Label>
                <Input type="number" value={form.dernier_km} onChange={set("dernier_km")} data-testid="suivi-dernier-km-input" />
              </div>
              <div className="space-y-2">
                <Label>Date dernière intervention</Label>
                <Input required type="date" value={form.derniere_date} onChange={set("derniere_date")} data-testid="suivi-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Intervalle (km)</Label>
                <Input type="number" value={form.intervalle_km} onChange={set("intervalle_km")} data-testid="suivi-intervalle-km-input" placeholder="15000" />
              </div>
              <div className="space-y-2">
                <Label>Intervalle (mois)</Label>
                <Input type="number" value={form.intervalle_mois} onChange={set("intervalle_mois")} data-testid="suivi-intervalle-mois-input" placeholder="12" />
              </div>
            </div>
            <Button type="submit" disabled={saving} data-testid="suivi-submit-btn"
              className="w-full rounded-full bg-blue-600 hover:bg-blue-700">
              {saving ? "Enregistrement…" : editSuiviId ? "Enregistrer les modifications" : "Enregistrer"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
